import axios from 'axios';
import { getAxiosAuthHeaders } from "../auth/axiosAuth";
import { Button } from '@mui/material';
import { useState } from 'react';

import * as React from "react";
import {
  List,
  Datagrid,
  TextField,
  DateField,
  ReferenceField,
  useRecordContext,
  Link,
  Edit,
  TextInput,
  SelectInput,
  required,
  TabbedForm,
  FormTab,
  SavedQueriesList,
  FilterList,
  FilterListItem,
  ReferenceManyField,
  Pagination,
  BooleanField,
  FunctionField,
  Filter
} from "react-admin";
import { useParams } from 'react-router-dom';
import { Card, CardContent } from "@mui/material";
import CustomDateField, { deletedRowStyle, UserLinkField } from "./users";
import { Avatar, Typography, Box } from "@mui/material";

import MailIcon from "@mui/icons-material/MailOutline";
import CategoryIcon from "@mui/icons-material/LocalOffer";
import { ChannelLinkField } from "./channels";

import { useDataProvider } from "react-admin";
import { useEffect } from 'react';
import { SimpleShowLayout } from 'react-admin';
import SetWorkspacePaymentPlanForm from "../components/SetWorkspacePaymentPlanForm";
import { Chip, IconButton, TextField as MuiTextField, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';



const DomainReferralSettings = ({ workspaceId }) => {
  const record = useRecordContext();
  const [domainReferralMode, setDomainReferralMode] = useState(record?.domain_referral_mode || 'do-not-inform');
  const [domainReferralMessage, setDomainReferralMessage] = useState(record?.domain_referral_message || '');
  const [domainReferralTitle, setDomainReferralTitle] = useState(record?.domain_referral_title || '');
  const [loading, setLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (record?.domain_referral_mode) {
      setDomainReferralMode(record.domain_referral_mode);
    }
    if (record?.domain_referral_message) {
      setDomainReferralMessage(record.domain_referral_message);
    }
    if (record?.domain_referral_title) {
      setDomainReferralTitle(record.domain_referral_title);
    }
    setHasChanges(false);
  }, [record?.domain_referral_mode, record?.domain_referral_message, record?.domain_referral_title]);

  const handleModeChange = (newMode) => {
    console.log('Changing mode from', domainReferralMode, 'to', newMode);
    setDomainReferralMode(newMode);
    setHasChanges(true);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const url = `${process.env.REACT_APP_API_BASE_URL}/v3/workspace`;
      const headers = await getAxiosAuthHeaders({
        'Content-Type': 'application/json',
      });
      
      const payload = {
        workspace_guid: workspaceId,
        name: record?.name || '',
        description: record?.description || null,
        domain_referral_mode: domainReferralMode,
        domain_referral_message: domainReferralMode === 'share-instructions' ? domainReferralMessage : '',
        domain_referral_title: domainReferralMode === 'share-instructions' ? domainReferralTitle : ''
      };
      
      console.log('Sending payload:', payload);
      await axios.put(url, payload, { headers });
      console.log('Successfully saved domain referral settings');
      setHasChanges(false);
    } catch (error) {
      console.error('Error saving domain referral settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMessageInputChange = (e) => {
    const newMessage = e.target.value;
    setDomainReferralMessage(newMessage);
    setHasChanges(true);
  };

  const handleTitleChange = (e) => {
    const newTitle = e.target.value;
    setDomainReferralTitle(newTitle);
    setHasChanges(true);
  };

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>Domain Referral Mode</Typography>
        <FormControl fullWidth disabled={loading}>
          <InputLabel>Domain Referral Mode</InputLabel>
          <Select
            value={domainReferralMode}
            onChange={(e) => handleModeChange(e.target.value)}
            label="Domain Referral Mode"
          >
            <MenuItem value="do-not-inform">Do Not Inform</MenuItem>
            <MenuItem value="automatic">Automatic</MenuItem>
            <MenuItem value="request">Request</MenuItem>
            <MenuItem value="share-instructions">Share Instructions</MenuItem>
          </Select>
        </FormControl>
      </Box>
      
      {domainReferralMode === 'share-instructions' && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>Domain Referral Title</Typography>
          <MuiTextField
            label="Domain Referral Title"
            value={domainReferralTitle}
            onChange={handleTitleChange}
            fullWidth
            disabled={loading}
          />
        </Box>
      )}
      
      {domainReferralMode === 'share-instructions' && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>Domain Referral Message</Typography>
          <MuiTextField
            label="Domain Referral Message"
            value={domainReferralMessage}
            onChange={handleMessageInputChange}
            fullWidth
            multiline
            rows={4}
            disabled={loading}
          />
        </Box>
      )}
      
      <Box sx={{ mt: 2 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={loading || !hasChanges}
          sx={{ mr: 1 }}
        >
          {loading ? 'Saving...' : 'Save Changes'}
        </Button>
        {hasChanges && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            You have unsaved changes
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const DomainsField = ({ workspaceId }) => {
  const record = useRecordContext();
  const [domains, setDomains] = useState(record?.domains || []);
  const [newDomain, setNewDomain] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (record?.domains) {
      setDomains(record.domains);
    }
  }, [record?.domains]);

  const addDomain = async () => {
    if (!newDomain.trim()) return;
    
    setLoading(true);
    try {
      const url = `${process.env.REACT_APP_API_BASE_URL}/v3/workspace/${workspaceId}/domain/${encodeURIComponent(newDomain.trim())}`;
      const headers = await getAxiosAuthHeaders();
      await axios.put(url, null, { headers });
      setDomains([...domains, newDomain.trim()]);
      setNewDomain('');
    } catch (error) {
      console.error('Error adding domain:', error);
    } finally {
      setLoading(false);
    }
  };

  const removeDomain = async (domainToRemove) => {
    setLoading(true);
    try {
      const url = `${process.env.REACT_APP_API_BASE_URL}/v3/workspace/${workspaceId}/domain/${encodeURIComponent(domainToRemove)}`;
      const headers = await getAxiosAuthHeaders();
      await axios.delete(url, { headers });
      setDomains(domains.filter(domain => domain !== domainToRemove));
    } catch (error) {
      console.error('Error removing domain:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      addDomain();
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <MuiTextField
          label="Add Domain"
          value={newDomain}
          onChange={(e) => setNewDomain(e.target.value)}
          onKeyPress={handleKeyPress}
          disabled={loading}
          fullWidth
          size="small"
        />
        <IconButton 
          onClick={addDomain} 
          disabled={loading || !newDomain.trim()}
          color="primary"
        >
          <AddIcon />
        </IconButton>
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {domains.map((domain, index) => (
          <Chip
            key={index}
            label={domain}
            onDelete={() => removeDomain(domain)}
            deleteIcon={<DeleteIcon />}
            disabled={loading}
            color="primary"
            variant="outlined"
          />
        ))}
      </Box>
    </Box>
  );
};

const AvatarField = ({ size = "25", sx }) => {
  const record = useRecordContext();
  if (!record) return null;
  return (
    <Avatar
      src={`${record.image_url}?size=${size}x${size}`}
      style={{ width: parseInt(size, 10), height: parseInt(size, 10) }}
      variant="rounded"
      sx={sx}

    />
  );
};

export const FullNameField = (props) => {
  const { size } = props;
  const record = useRecordContext();
  return record ? (
    <Typography
      variant="body2"
      display="flex"
      flexWrap="nowrap"
      alignItems="center"
      component="div"
      sx={props.sx}
    >
      <AvatarField
        record={record}
        size={size}
        sx={{
          mr: 1,
          mt: -0.5,
          mb: -0.5,
        }}
      />
      {record.name}
    </Typography>
  ) : null;
};
export const WorkspaceLinkField = (props) => {
  const record = useRecordContext();
  if (!record) {
    return null;
  }
  return (
    <Link to={`/workspaces/${record.id}`}>
      <FullNameField />
    </Link>
  );
};

const workspaceFilters = [
  <TextInput label="Workspace ID" source="id" alwaysOn />,
  <TextInput label="Workspace Name" source="name" alwaysOn />
];

export const WorkspaceFilterSidebar = () => (
  <Card
    sx={{ order: -1, flex: "0 0 15em", mr: 2, mt: 8, alignSelf: "flex-start" }}
  >
    <CardContent sx={{ pt: 1 }}>
      <SavedQueriesList />
      <FilterList label="Type" icon={<MailIcon />}>
        <FilterListItem label="standard" value={{ type: "standard" }} />
        <FilterListItem label="webcontact" value={{ type: "webcontact" }} />
        <FilterListItem label="personallink" value={{ type: "personallink" }} />
      </FilterList>
      <FilterList label="Plan" icon={<CategoryIcon />}>
        <FilterListItem
          label="Business-premium"
          value={{ plan_type: "business-premium" }}
        />
      </FilterList>
    </CardContent>
  </Card>
);

export const WorkspaceList = (props) => (
  <List {...props} aside={<WorkspaceFilterSidebar />} filters={workspaceFilters} sort={{ field: 'name', order: 'ASC' }}>
    <Datagrid rowClick="edit" rowStyle={deletedRowStyle}>
      <TextField source="id" />
      <WorkspaceLinkField source="name" label="Workspace name" />
      <TextField source="description" />
      <ReferenceField source="owner_id" reference="users">
        <UserLinkField />
      </ReferenceField>
      <TextField source="type" />
      <CustomDateField source="last_updated_on" label="Updated" />
      <CustomDateField source="created_at"  />
      <CustomDateField source="deleted_on"  />
    </Datagrid>
  </List>
);

const WorkspaceTitle = () => {
  const record = useRecordContext();
  return record ? (
    <span>
      {record.name} ({record.id})
    </span>
  ) : null;
};

const PaymentSettings = (props) =>  {
  
  const record = useRecordContext(); // Assuming this provides your record data
  const dataProvider = useDataProvider();
  
  const [paymentPlans, setPaymentPlans] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!record?.id) return;

    const fetchPaymentPlans = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await dataProvider.getPaymentPlanForWorkspace({ id: record.id });
        setPaymentPlans(response.data);
      } catch (err) {
        setError(err.message || "An error occurred while fetching payment plans.");
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentPlans();
  }, [dataProvider, record?.id]);

  return (
    <SimpleShowLayout>
      <SetWorkspacePaymentPlanForm workspaceId={record.id} />
      
          
          {loading && <p>Loading payment plans...</p>}
          {error && <p>Error: {error}</p>}
          {!loading && !error && paymentPlans && (
            <>
          <h4>Payment Plan Summary</h4>
          <a href={`https://dashboard.stripe.com/customers/${record.stripe_customer_id}`} target="_blank">Go To Stripe</a>
              <ul>
                {Object.entries(paymentPlans.payment_plan_summary).map(([key, value]) => (
                  <li key={key}>
                    <strong>{key}:</strong>{" "}
                    {Array.isArray(value)
                      ? value.join(", ")
                      : value !== null
                      ? value.toString()
                      : "-"}
                  </li>
                ))}
              </ul>
              <h4>Managed Plans</h4>
              {paymentPlans.managed_plans.length > 0 ? (
                <ul>
                  {paymentPlans.managed_plans.map((plan, index) => (
                    <li key={index}>
                      {JSON.stringify(plan, null, 2)} {/* Display plan details */}
                    </li>
                  ))}
                </ul>
              ) : (
                <p>No managed plans available.</p>
              )}
            </>
          )}
       
    </SimpleShowLayout>
  );
};


const req = [required()];

const UserStatusField = ({ record, workspaceRecord }) => {
  const referencedUserId = record._id; // Assuming user_id is the identifier
  const user = workspaceRecord?.users.find((user) => user._id === referencedUserId);
  const userStatus = user?.status;
  return userStatus || 'N/A';
};

const RoleFilter = (props) => (
  <Filter {...props}>
    <TextInput label="Role" source="role" alwaysOn />
  </Filter>
);

export const WorkspaceEdit = () => {

  const { id } = useParams(); // Access the 'id' parameter from the URL
  const [updateStatus, setUpdateStatus] = useState('default'); // Track the update status

  // Handle the update workspace updated at status
  const handleUpdate = async () => {
    try {
      const url = `${process.env.REACT_APP_API_BASE_URL}/admin/workspace/updatedAt/${id}`;
      const headers = await getAxiosAuthHeaders();
      await axios.put(url, null, {headers});
      setUpdateStatus('updated'); // Update the status on success
    } catch (error) {
      console.error('Error updating workspace:', error);
      // Handle the error appropriately
    }
  };



  return (
    <Edit title={<WorkspaceTitle />}>
      <TabbedForm toolbar={false}>
        <FormTab label="Info">
          <TextInput source="name" fullWidth validate={req} />
          <TextInput source="description" fullWidth validate={req} />
          {/* Update workspace updated_on value */}
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleUpdate}
          >
            {updateStatus === 'updated' ? 'Updated!' : 'Refresh Updated At'}
          </Button>
        </FormTab>
        <FormTab label="Users">

          <ReferenceManyField
            reference="users"
            target="workspace_roles.workspace_id"
            pagination={<Pagination />}
            filters={<RoleFilter />}
          >
            <Datagrid
              sx={{
                width: "100%",
                "& .column-comment": {
                  maxWidth: "20em",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                },

              }}
            >
              <UserLinkField />
              <FunctionField
                label="Role"
                sortable={true}
                render={(record) => {

                  const workspaceRole = record.workspace_roles.find((role) => {

                    return role.workspace_id === id
                  });
                  return workspaceRole ? workspaceRole.role : '';
                }}

              />


              <CustomDateField source="last_seen_on" />
              <CustomDateField source="created_at" />
              <CustomDateField source="deleted_on" />
            </Datagrid>
          </ReferenceManyField>
        </FormTab>


        <FormTab label="Conversations">
          <ReferenceManyField
            reference="channels"
            target="workspace_id"
            pagination={<Pagination />}
          >
            <Datagrid
              sx={{
                width: "100%",
                "& .column-comment": {
                  maxWidth: "20em",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                },
              }}
            >
              <TextField source="id" />
              <ChannelLinkField source="name" />
              <TextField source="description" />
              <ReferenceField source="owner_id" reference="users">
                <UserLinkField />
              </ReferenceField>
              <FunctionField
                source="users"
                render={(record) => record.users.length}
              />
              <TextField source="visibility" />
              <CustomDateField source="deleted_on" />
              <CustomDateField source="sort_order" />
            </Datagrid>
          </ReferenceManyField>
        </FormTab>

        <FormTab label="Payment Settings">
          <PaymentSettings />
        </FormTab>

        <FormTab label="Domain Referral">
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Domains</Typography>
            <DomainsField workspaceId={id} />
          </Box>
          <DomainReferralSettings workspaceId={id} />
        </FormTab>


      </TabbedForm>
    </Edit>
  )
};
